// Phase 4 follow-up #3 — left rail: paginated catalog with thumb,
// title, price, source pill, draft pill, match count, and a small
// filter row at top (search + source select + drafts toggle).
// Catalog-level re-scrape lives in the header chrome (brand-scoped),
// not on the per-product Header.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Flex, Text, Image, VStack, HStack, Badge, Button, Spinner, Input, Select,
  IconButton, Icon, Checkbox, useDisclosure, useToast,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay
} from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import type { CatalogListRow, CatalogDetailResponse } from './types';
import { sourceTone, formatPrice, timeAgo } from './format';
import { AgentAttentionMark } from '../../components/agentShell/AgentAttentionMark';
import { AgentInlineNudge }   from '../../components/agentShell/AgentInlineNudge';
import { attentionMarksEnabled, nudgesEnabled } from '../../components/agentShell/flags';
import { selectAttentionTargets, useSurfaceContext } from '../../components/agentShell/useSurfaceContext';
import { isAgentV2Route } from '../../lib/featureFlags';

type Filters = {
  q?:           string;
  source?:      string;
  category?:    string;
  inStock?:     boolean;
  hasReviews?:  boolean;
  showVariants?: boolean;
};

type Props = {
  rows:           CatalogListRow[];
  total:          number;
  totalDrafts:    number;
  loading:        boolean;
  hasMore:        boolean;
  selectedId:     string | null;
  filters:        Filters;
  categories:     string[];
  // Full detail for the currently-selected product. Drives the alt
  // fanout — additionalImages render as subordinate cards under the
  // parent SidebarRow. null when nothing's selected or detail's still
  // loading; the row renders without children in that case.
  selectedDetail: CatalogDetailResponse | null;
  // null = parent hero is the gallery's active item; number = that
  // additionalImages index is active. Drives the highlight on alt
  // cards and is set by clicking one.
  activeAltIndex: number | null;
  onSelect:       (productId: string) => void;
  onAltSelect:    (altIndex: number | null) => void;
  onLoadMore:     () => void;
  onFilters:      (next: Filters) => void;
  // Silent list reload after a catalog re-scrape finishes.
  onRefresh:      () => void;
};

export function Sidebar({
  rows, total, totalDrafts, loading, hasMore, selectedId, filters, categories,
  selectedDetail, activeAltIndex,
  onSelect, onAltSelect, onLoadMore, onFilters, onRefresh
}: Props) {
  // M7.3 — marks and nudges render only on an explicit /v2 route.
  // The live /catalog path never mounts them and the hook does not
  // fetch there. Each layer has its own flag. One useSurfaceContext
  // call per mount; marks are selected from that payload.
  const { pathname } = useLocation();
  const onV2 = isAgentV2Route(pathname);
  const showNudges = onV2 && nudgesEnabled();
  const showMarks = onV2 && attentionMarksEnabled();
  const ctx = useSurfaceContext({ surface: 'catalog-browser' });
  const attention = useMemo(
    () => selectAttentionTargets(showMarks ? ctx.data : null, 'product'),
    [showMarks, ctx.data]
  );
  const nudges = showNudges && Array.isArray(ctx.data?.nudges)
    ? ctx.data.nudges.filter(nudge => nudge && typeof nudge.id === 'string')
    : [];
  return (
    <Flex
      direction="column"
      w="340px"
      flexShrink={0}
      bg="brand.surface"
      borderRightWidth="1px"
      borderRightColor="brand.border"
      h="100%"
      minH={0}
    >
      <Box px={4} py={3} borderBottomWidth="1px" borderBottomColor="brand.border">
        <HStack justify="space-between" mb={2} align="flex-start">
          <Text fontSize="md" fontWeight="800" color="brand.ink">Catalog</Text>
          <HStack spacing={1} align="center" flexShrink={0}>
            <Text fontSize="xs" color="brand.muted">{total}</Text>
            {totalDrafts > 0 && (
              <Badge variant="subtle" colorScheme="orange" fontSize="9px">{totalDrafts} draft{totalDrafts === 1 ? '' : 's'}</Badge>
            )}
          </HStack>
        </HStack>

        <RescrapeControl onRefresh={onRefresh} />

        <VStack spacing={2} align="stretch">
          <HStack spacing={1}>
            <Box position="relative" flex={1}>
              <Input
                size="sm"
                placeholder="Search title or description…"
                value={filters.q || ''}
                onChange={e => onFilters({ ...filters, q: e.target.value || undefined })}
              />
              {filters.q && (
                <IconButton
                  aria-label="Clear search"
                  size="xs"
                  variant="ghost"
                  position="absolute"
                  right={1}
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={() => onFilters({ ...filters, q: undefined })}
                  icon={<Box as="span" fontSize="11px">×</Box>}
                />
              )}
            </Box>
          </HStack>
          <HStack spacing={2}>
            <Select
              size="xs"
              value={filters.source || ''}
              onChange={e => onFilters({ ...filters, source: e.target.value || undefined })}
            >
              <option value="">All sources</option>
              <option value="ig-catalog">Meta Catalog</option>
              <option value="shopify-app">Shopify</option>
              <option value="shopify-direct">Shopify (public)</option>
              <option value="apify-shopify">Shopify (via Apify)</option>
              <option value="sitemap-jsonld">Website scrape</option>
              <option value="manual-upload">Manual</option>
              <option value="detect-identified">Detect-identified</option>
              <option value="draft">Drafts only</option>
            </Select>
            {categories.length > 0 && (
              <Select
                size="xs"
                value={filters.category || ''}
                onChange={e => onFilters({ ...filters, category: e.target.value || undefined })}
              >
                <option value="">All categories</option>
                {categories.slice(0, 50).map(c => <option key={c} value={c}>{c}</option>)}
              </Select>
            )}
          </HStack>
          <Checkbox
            size="sm"
            isChecked={!!filters.showVariants}
            onChange={e => onFilters({ ...filters, showVariants: e.target.checked || undefined })}
          >
            <Text fontSize="xs" color="brand.muted">Show all variants</Text>
          </Checkbox>
        </VStack>
      </Box>

      <Box flex={1} overflowY="auto" px={3} py={3}>
        {/* M7.3 — agent inline nudges. Empty when the surface-context
            endpoint hasn't shipped nudges (flag off, no brand, or
            none proposed), so the list collapses to zero-height and
            the sidebar looks identical to pre-M7.3 trunk. */}
        {nudges.length > 0 && (
          <VStack align="stretch" spacing={2} mb={3}>
            {nudges.map(n => (
              <AgentInlineNudge key={n.id} nudge={n} />
            ))}
          </VStack>
        )}
        {loading && rows.length === 0 ? (
          <Flex align="center" justify="center" py={10}><Spinner color="brand.muted" /></Flex>
        ) : rows.length === 0 ? (
          <Box px={3} py={10} textAlign="center">
            <Text fontSize="sm" color="brand.muted">No catalog products match.</Text>
            <Text fontSize="xs" color="brand.muted" mt={2}>Try clearing the filters or syncing a Meta Catalog.</Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={2}>
            {rows.map(r => {
              const isActive = r.id === selectedId;
              const detailProduct = isActive ? selectedDetail?.product : null;
              const altUrls = isActive && detailProduct?.additionalImages
                ? detailProduct.additionalImages.filter(u => u && u !== detailProduct.imageUrl)
                : [];
              const isAttentionTarget = attention.ids.has(r.id);
              const attentionReason   = isAttentionTarget ? attention.reasonFor(r.id) : undefined;
              return (
                <Box key={r.id}>
                  {isAttentionTarget ? (
                    <AgentAttentionMark reason={attentionReason}>
                      <SidebarRow
                        row={r}
                        isActive={isActive}
                        parentHeroActive={isActive && activeAltIndex === null}
                        onClick={() => onSelect(r.id)}
                        onParentHeroClick={isActive ? () => onAltSelect(null) : undefined}
                      />
                    </AgentAttentionMark>
                  ) : (
                    <SidebarRow
                      row={r}
                      isActive={isActive}
                      parentHeroActive={isActive && activeAltIndex === null}
                      onClick={() => onSelect(r.id)}
                      onParentHeroClick={isActive ? () => onAltSelect(null) : undefined}
                    />
                  )}
                  {isActive && altUrls.length > 0 && (
                    <Box pl={6} pt={1.5}>
                      <VStack align="stretch" spacing={1.5}>
                        {altUrls.map((url, idx) => (
                          <AltRow
                            key={`${r.id}-alt-${idx}`}
                            url={url}
                            label={`Alt ${idx + 1}`}
                            isActive={activeAltIndex === idx}
                            onClick={() => onAltSelect(idx)}
                          />
                        ))}
                      </VStack>
                    </Box>
                  )}
                </Box>
              );
            })}
            {hasMore && (
              <Button onClick={onLoadMore} isLoading={loading} variant="ghost" size="sm" mt={2}>
                Load more
              </Button>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
}

function SidebarRow({
  row, isActive, parentHeroActive, onClick, onParentHeroClick
}: {
  row:               CatalogListRow;
  isActive:          boolean;
  parentHeroActive?: boolean;        // true when this parent's hero is the gallery's active item
  onClick:           () => void;
  onParentHeroClick?: () => void;    // when isActive AND user clicks the row, swap gallery back to parent hero
}) {
  const tone = sourceTone(row.source);
  // Two-click affordance for the active row: clicking it when NOT yet
  // active selects it. Clicking it when active (i.e. user wants to
  // revert from an alt back to the parent hero) calls onParentHeroClick.
  const handleClick = isActive ? (onParentHeroClick || onClick) : onClick;
  // Distinct outline when the parent hero is the gallery's current view
  // (vs. parent selected but an alt is being shown in the gallery).
  const ringColor = isActive
    ? (parentHeroActive ? 'rsViolet.400' : 'rsViolet.200')
    : 'brand.border';
  return (
    <Box
      onClick={handleClick}
      cursor="pointer"
      borderRadius="lg"
      borderWidth={parentHeroActive ? '2px' : '1px'}
      borderColor={ringColor}
      bg={isActive ? 'rsViolet.50' : 'brand.surface'}
      p={2}
      transition="border-color 120ms, background 120ms"
      _hover={{ borderColor: isActive ? 'rsViolet.300' : 'gray.300' }}
    >
      <HStack spacing={3} align="stretch">
        <Box w="56px" h="56px" flexShrink={0} borderRadius="md" overflow="hidden" bg="gray.100">
          {row.imageUrl && <Image src={row.imageUrl} alt={row.title} w="100%" h="100%" objectFit="cover" loading="lazy" />}
        </Box>
        <Box flex={1} minW={0}>
          <Text fontSize="sm" fontWeight="700" color="brand.ink" noOfLines={1}>{row.title}</Text>
          <HStack spacing={2} mt={0.5}>
            <Text fontSize="xs" color="brand.muted">{formatPrice(row.price, row.currency)}</Text>
            {typeof row.rating === 'number' && (
              <HStack spacing={0.5}>
                <StarIcon />
                <Text fontSize="xs" color="brand.muted">{row.rating.toFixed(1)}</Text>
              </HStack>
            )}
          </HStack>
          <HStack mt={1} spacing={1.5} wrap="wrap">
            <Badge fontSize="9px" variant="subtle" px={1.5} borderRadius="md" style={{ background: tone.bg, color: tone.fg }}>
              {tone.label}
            </Badge>
            {row.draft && <Badge fontSize="9px" variant="subtle" colorScheme="orange">Draft</Badge>}
            {row.variantCount > 0 && (
              <Badge fontSize="9px" variant="subtle" colorScheme="blue">+{row.variantCount} variant{row.variantCount === 1 ? '' : 's'}</Badge>
            )}
            {!row.isPrimaryVariant && (
              <Badge fontSize="9px" variant="subtle" colorScheme="gray">Variant</Badge>
            )}
            {row.matchCount > 0 && <Badge fontSize="9px" variant="subtle" colorScheme="purple">{row.matchCount} match{row.matchCount === 1 ? '' : 'es'}</Badge>}
            {/* Shopify push audit badge — only renders when at least one
                lifestyle image has been pushed back to the merchant's
                Shopify PDP. Matches sourceTone's shopify-app green so
                a scan of the row visually ties "source: Shopify" and
                "I pushed stuff back" together.                       */}
            {row.shopifySource && row.shopifySource.pushTargets.length > 0 && (
              <Badge
                fontSize="9px"
                variant="subtle"
                px={1.5}
                borderRadius="md"
                style={{ background: 'rgba(149,191,72,0.18)', color: '#4A8730' }}
                title={`${row.shopifySource.pushTargets.length} image(s) pushed to this product in Shopify`}
              >
                ↑ Pushed {row.shopifySource.pushTargets.length}
              </Badge>
            )}
            <Text fontSize="9px" color="brand.muted">{timeAgo(row.lastSyncedAt)}</Text>
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}

function StarIcon() { return <Icon viewBox="0 0 24 24" w="10px" h="10px" color="#F59E0B"><path fill="currentColor" d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.5l-6.3 4.5L8 13.9 2 9.4h7.6z" /></Icon>; }

// Catalog re-scrape — product catalog only (not a full demo sync).
// Mirrors SalesDemos' fire-and-forget + poll pattern: POST returns
// 202 with a runId, then we poll GET /api/progress/:runId until
// terminal status / timeout / unmount.
const RESCRAPE_POLL_MS     = 2500;
const RESCRAPE_TIMEOUT_MS  = 10 * 60 * 1000;

type ProgressSnapshot = {
  status:     'running' | 'succeeded' | 'failed' | 'cancelled' | string;
  stage?:     string | null;
  note?:      string | null;
  itemsDone?: number | null;
  itemsTotal?: number | null;
  pct?:       number | null;
  error?:     string | null;
};

type RescrapeStart = {
  ok:      boolean;
  runId?:  string;
  brandId?: string;
  origin?: string;
  error?:  string;
};

type RescrapeOutcome = {
  status:  'succeeded' | 'failed' | 'cancelled' | 'timeout';
  error?:  string | null;
};

function RescrapeControl({ onRefresh }: { onRefresh: () => void }) {
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.id || null;
  const brandName = activeBrand?.name || 'this brand';
  const toast = useToast();
  const confirm = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [running, setRunning]   = useState(false);
  const [stage, setStage]       = useState<string | null>(null);
  const [note, setNote]         = useState<string | null>(null);
  const [outcome, setOutcome]   = useState<RescrapeOutcome | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  // Poller cancellation: set true on unmount, brand switch, or a new start.
  const stopRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const brandAtStartRef = useRef<string | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Stop polling on unmount so a wedged tab cannot keep hitting the API.
  useEffect(() => {
    stopRef.current = false;
    return () => {
      stopRef.current = true;
      clearTimer();
    };
  }, [clearTimer]);

  // Brand switch abandons any in-flight UI; the server job continues.
  useEffect(() => {
    stopRef.current = true;
    clearTimer();
    setRunning(false);
    setStage(null);
    setNote(null);
    setOutcome(null);
    setStartError(null);
    stopRef.current = false;
  }, [brandId, clearTimer]);

  const pollProgress = useCallback((runId: string, startedAt: number) => {
    const tick = async () => {
      if (stopRef.current) return;
      if (brandAtStartRef.current !== brandId) return;

      try {
        const snap = await apiJson<ProgressSnapshot>(`/api/progress/${encodeURIComponent(runId)}`);
        if (stopRef.current || brandAtStartRef.current !== brandId) return;

        setStage(snap.stage || null);
        setNote(snap.note || null);

        const status = snap.status;
        if (status === 'succeeded' || status === 'failed' || status === 'cancelled') {
          setRunning(false);
          setStage(null);
          setNote(null);
          if (status === 'succeeded') {
            setOutcome({ status: 'succeeded' });
            toast({
              status: 'success',
              title: 'Catalog re-scrape complete',
              description: 'Product data updated. Refreshing the list…',
              duration: 4000
            });
            onRefresh();
          } else if (status === 'failed') {
            const err = snap.error || 'Re-scrape failed';
            setOutcome({ status: 'failed', error: err });
            toast({
              status: 'error',
              title: 'Catalog re-scrape failed',
              description: err,
              duration: 8000,
              isClosable: true
            });
          } else {
            setOutcome({ status: 'cancelled' });
            toast({
              status: 'warning',
              title: 'Catalog re-scrape cancelled',
              duration: 4000
            });
          }
          return;
        }
      } catch {
        // Transient poll blip — retry until timeout. Auth redirects are
        // handled inside apiFetch and will navigate away.
      }

      if (stopRef.current || brandAtStartRef.current !== brandId) return;
      if (Date.now() - startedAt > RESCRAPE_TIMEOUT_MS) {
        setRunning(false);
        setStage(null);
        setNote(null);
        setOutcome({
          status: 'timeout',
          error: 'Still running after 10 minutes. Check Activity or try again later.'
        });
        toast({
          status: 'warning',
          title: 'Re-scrape still running',
          description: 'Taking longer than expected. Check the Activity dock or refresh in a few minutes.',
          duration: 7000,
          isClosable: true
        });
        return;
      }
      timerRef.current = window.setTimeout(tick, RESCRAPE_POLL_MS);
    };
    timerRef.current = window.setTimeout(tick, RESCRAPE_POLL_MS);
  }, [brandId, onRefresh, toast]);

  const startRescrape = async () => {
    if (!brandId || running) return;
    confirm.onClose();
    stopRef.current = false;
    brandAtStartRef.current = brandId;
    clearTimer();
    setRunning(true);
    setStage('starting…');
    setNote(null);
    setOutcome(null);
    setStartError(null);

    try {
      // Fire-and-forget: 202 returns immediately; work continues server-side.
      const res = await apiJson<RescrapeStart>(
        `/api/catalog/brands/${encodeURIComponent(brandId)}/rescrape`,
        { method: 'POST' }
      );
      if (stopRef.current || brandAtStartRef.current !== brandId) return;
      if (!res?.runId) {
        throw new Error(res?.error || 'No runId returned from re-scrape');
      }
      toast({
        status: 'info',
        title: 'Catalog re-scrape started',
        description: 'Re-scraping products only — Instagram posts and reviews are not touched.',
        duration: 4000
      });
      pollProgress(res.runId, Date.now());
    } catch (err) {
      if (stopRef.current || brandAtStartRef.current !== brandId) return;
      // Surface the server's error string (e.g. no catalog URL, already running).
      const msg = err instanceof Error ? err.message : 'Failed to start re-scrape';
      setRunning(false);
      setStage(null);
      setNote(null);
      setStartError(msg);
      setOutcome({ status: 'failed', error: msg });
      toast({
        status: 'error',
        title: 'Could not start re-scrape',
        description: msg,
        duration: 7000,
        isClosable: true
      });
    }
  };

  const disabled = !brandId || running;
  const statusLine = running
    ? [stage, note].filter(Boolean).join(' · ') || 'Re-scraping catalog…'
    : startError
      ? startError
      : outcome?.status === 'failed'
        ? (outcome.error || 'Re-scrape failed')
        : outcome?.status === 'timeout'
          ? (outcome.error || 'Timed out waiting for re-scrape')
          : outcome?.status === 'succeeded'
            ? 'Catalog updated'
            : outcome?.status === 'cancelled'
              ? 'Re-scrape cancelled'
              : null;
  const statusColor = running
    ? 'brand.muted'
    : outcome?.status === 'succeeded'
      ? 'green.600'
      : (outcome?.status === 'failed' || outcome?.status === 'timeout' || startError)
        ? 'red.500'
        : 'brand.muted';

  return (
    <Box mb={2}>
      <Button
        size="xs"
        variant="outline"
        w="100%"
        onClick={confirm.onOpen}
        isDisabled={disabled}
        isLoading={running}
        loadingText="Re-scraping…"
      >
        Re-scrape catalog
      </Button>
      {statusLine && (
        <HStack spacing={1.5} mt={1.5} align="flex-start">
          {running && <Spinner size="xs" color="brand.muted" mt="2px" flexShrink={0} />}
          <Text fontSize="10px" color={statusColor} lineHeight="1.3" noOfLines={3}>
            {statusLine}
          </Text>
        </HStack>
      )}
      {!brandId && (
        <Text fontSize="10px" color="brand.muted" mt={1}>
          Select a brand to re-scrape its catalog.
        </Text>
      )}

      <AlertDialog
        isOpen={confirm.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={confirm.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="md" fontWeight="800" color="brand.ink">
              Re-scrape product catalog?
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={2}>
                <Text fontSize="sm" color="brand.ink">
                  This re-scrapes the <Text as="span" fontWeight="700">product catalog</Text> for{' '}
                  <Text as="span" fontWeight="700">{brandName}</Text>, replacing product data
                  (titles, prices, images, variants). It can take several minutes.
                </Text>
                <Text fontSize="sm" color="brand.muted">
                  It does <Text as="span" fontWeight="700">not</Text> pull Instagram posts,
                  reviews, or run enrichment — that is a full demo sync, not this action.
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} size="sm" variant="ghost" onClick={confirm.onClose}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="purple" ml={3} onClick={() => { void startRescrape(); }}>
                Re-scrape catalog
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

// Subordinate card for an alt image under the active product. Smaller
// thumbnail + label only — the right-rail tabs inherit the parent's
// data so we don't need to repeat title/price/etc. here. Distinct
// highlight when this alt is the gallery's active item. Indented via
// the parent VStack's pl, plus a connecting line for visual hierarchy.
function AltRow({
  url, label, isActive, onClick
}: {
  url:      string;
  label:    string;
  isActive: boolean;
  onClick:  () => void;
}) {
  return (
    <Box
      onClick={onClick}
      cursor="pointer"
      borderRadius="md"
      borderWidth={isActive ? '2px' : '1px'}
      borderColor={isActive ? 'rsViolet.400' : 'brand.border'}
      bg={isActive ? 'rsViolet.50' : 'brand.surface'}
      p={1.5}
      transition="border-color 120ms, background 120ms"
      _hover={{ borderColor: isActive ? 'rsViolet.400' : 'gray.300' }}
      position="relative"
    >
      <HStack spacing={2.5} align="center">
        {/* Connector dash so the parent → alt relationship reads as a tree */}
        <Box
          position="absolute"
          left="-12px"
          top="50%"
          w="8px"
          h="1px"
          bg="brand.border"
        />
        <Box w="36px" h="36px" flexShrink={0} borderRadius="sm" overflow="hidden" bg="gray.100">
          <Image src={url} alt={label} w="100%" h="100%" objectFit="cover" loading="lazy" />
        </Box>
        <Text fontSize="xs" color={isActive ? 'rsViolet.700' : 'brand.muted'} fontWeight={isActive ? '700' : '500'} noOfLines={1}>
          {label}
        </Text>
      </HStack>
    </Box>
  );
}
